/**
 * Client side of the partner context: the partner(s) the middleware injected
 * into this page (`./partner-context.ts`). This is the ONLY way client code
 * learns about partners. Never import `./partners.ts`,
 * `./reseller-partners.ts` (types excepted) or a partner brand file from
 * client code: they hold every partner and would ship them to all hosts.
 *
 *  - `currentPartner()`: the partner serving this host, null on Tagada hosts.
 *  - `redirectPartner()`: on Tagada hosts, the partner a sign-in bounce came
 *    from (its `redirect_url`). Kept in sessionStorage so the OAuth return
 *    (`/sso-callback`, no query) still knows it.
 *  - `resellerPartners()`: reseller-only partners this host may render.
 */

import type { BrandConfig } from './brands/types';
import {
  hostBelongsToRoot,
  partnerOrigins,
  REDIRECT_PARTNER_ENDPOINT,
  type InjectedPartner,
} from './partner-context';
import type { ResellerPartnerConfig } from './reseller-partners';
import { whitelabelResellers } from './brands/whitelabel';

const REDIRECT_PARTNER_STORAGE_KEY = 'tgd_redirect_partner';

const isInjectedPartner = (v: unknown): v is InjectedPartner => {
  if (!v || typeof v !== 'object') return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.brand === 'string' &&
    typeof p.rootDomain === 'string' &&
    typeof p.appHost === 'string' &&
    typeof p.clerkFapi === 'string'
  );
};

const hasWindow = () => typeof window !== 'undefined';

/** The partner serving the current host, or null (Tagada, local, preview and unknown hosts). */
export function currentPartner(): InjectedPartner | null {
  if (!hasWindow()) return null;
  const p = window.__TAGADA_PARTNER__;
  return isInjectedPartner(p) ? p : null;
}

/** Brand key of the current host's partner, for signup attribution. Null off partner hosts. */
export function currentPartnerBrand(): string | null {
  return currentPartner()?.brand ?? null;
}

const readStoredRedirectPartner = (): InjectedPartner | null => {
  try {
    const raw = window.sessionStorage.getItem(REDIRECT_PARTNER_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isInjectedPartner(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const storeRedirectPartner = (p: InjectedPartner): void => {
  try {
    window.sessionStorage.setItem(REDIRECT_PARTNER_STORAGE_KEY, JSON.stringify(p));
  } catch {
    // Storage disabled: the injected value still serves this page.
  }
};

let resolvedRedirectPartner: InjectedPartner | null | undefined;

/**
 * On a Tagada host, the partner whose sign-in bounced the user here: the one
 * injected for this page's `redirect_url`, else the one remembered from an
 * earlier page of this tab. Always null on partner hosts.
 */
export function redirectPartner(): InjectedPartner | null {
  if (!hasWindow() || currentPartner()) return null;
  if (resolvedRedirectPartner !== undefined) return resolvedRedirectPartner;
  const injected = window.__TAGADA_REDIRECT_PARTNER__;
  if (isInjectedPartner(injected)) {
    storeRedirectPartner(injected);
    resolvedRedirectPartner = injected;
    return injected;
  }
  resolvedRedirectPartner = readStoredRedirectPartner();
  return resolvedRedirectPartner;
}

/** The partners this page knows: the host's own, or the redirect one on Tagada hosts. */
const knownPartners = (): InjectedPartner[] =>
  [currentPartner(), redirectPartner()].filter((p): p is InjectedPartner => p !== null);

/** The known partner whose rootDomain matches `host`, or null. */
export function knownPartnerByHost(host: string | null | undefined): InjectedPartner | null {
  if (!host) return null;
  return knownPartners().find((p) => hostBelongsToRoot(host, p.rootDomain)) ?? null;
}

/** The known partner whose `https://<appHost>` is the origin of `url`, or null. */
export function knownPartnerByOrigin(url: string | null | undefined): InjectedPartner | null {
  if (!url) return null;
  try {
    const origin = new URL(url).origin;
    return knownPartners().find((p) => origin === `https://${p.appHost}`) ?? null;
  } catch {
    return null;
  }
}

/** Own origins (app + dashboard) of the known partners: redirect allowlists. */
export function knownPartnerOrigins(): string[] {
  return knownPartners().flatMap(partnerOrigins);
}

/** Code brand of the known partner whose app origin is `url`'s, or null. */
export function knownPartnerBrandByOrigin(url: string | null | undefined): BrandConfig | null {
  return knownPartnerByOrigin(url)?.brandConfig ?? null;
}

/** Reseller-only partners this host may render, by org whitelabel slug. */
export function resellerPartners(): Record<string, ResellerPartnerConfig> {
  const injected = hasWindow() ? window.__TAGADA_RESELLERS__ : undefined;
  return { ...whitelabelResellers, ...(injected && typeof injected === 'object' ? injected : {}) };
}

/** `redirect_url` carried by the URL fragment (`#/?redirect_url=`), which never reaches the server. */
const redirectUrlFromHash = (): string | null => {
  if (!window.location.hash) return null;
  const hash = window.location.hash.replace(/^#\/?/, '');
  const qIdx = hash.indexOf('?');
  try {
    return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : hash).get('redirect_url');
  } catch {
    return null;
  }
};

const REDIRECT_LOOKUP_TIMEOUT_MS = 2500;

/**
 * Before the first render on a Tagada host: when the `redirect_url` sits in
 * the fragment (Clerk's account-portal form, invisible to the middleware),
 * ask the middleware which partner owns it. Never rejects; a failed lookup
 * leaves the redirect partner as it was (the redirect is then refused, as an
 * unknown origin always was).
 */
export async function resolveRedirectPartnerFromHash(): Promise<void> {
  if (!hasWindow() || currentPartner()) return;
  const url = redirectUrlFromHash();
  if (!url) return;
  let origin: string;
  try {
    origin = new URL(url).origin;
  } catch {
    return;
  }
  if (knownPartnerOrigins().includes(origin)) return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REDIRECT_LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${REDIRECT_PARTNER_ENDPOINT}?url=${encodeURIComponent(url)}`, {
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
    if (!res.ok) return;
    const body = (await res.json()) as { partner?: unknown } | null;
    if (isInjectedPartner(body?.partner)) {
      storeRedirectPartner(body.partner);
      resolvedRedirectPartner = body.partner;
    }
  } catch {
    // Network error or timeout: keep what we had.
  } finally {
    clearTimeout(timer);
  }
}

/** Test seam: forget the memoised redirect partner. */
export function resetPartnerContextCacheForTests(): void {
  resolvedRedirectPartner = undefined;
}
